import { snakeCase } from 'lodash/string';
import {
  CARDINALITIES,
  LISTING_CATEGORIES,
  LISTING_METADATA,
  LISTING_TYPES,
  SPORTS_CLUB_SUB_CATEGORIES,
  USER_TYPES,
} from './listingTypeConfig';
import { isFieldForCategory, isFieldForListingType } from '../../util/fieldHelpers';

// Utility functions
/**
 * Get all subcategories for a given category ID
 * @param {string} categoryId
 * @returns {Array} Subcategories or an empty array if not found
 */
export const getSubCategoriesByCategoryId = categoryId => {
  const category = LISTING_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.subCategories : [];
};
/**
 * Find a subcategory by its ID across all categories
 * @param {string} subCategoryId
 * @returns {Object|null} Subcategory object or null if not found
 */
export const getSubCategoryById = subCategoryId => {
  for (const category of LISTING_CATEGORIES) {
    const subCategory = category.subCategories.find(sub => sub.id === subCategoryId);
    if (subCategory) {
      return subCategory;
    }
  }
  return null;
};
/**
 * Returns listing types that a user can create based on their user type and listing configuration.
 * Filters out listing types with a cardinality of NONE.
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.currentUser - The current user object
 * @returns {Array} - Array of listing types that the user can create
 */
export const getListingTypesUserCanCreate = ({ currentUser }) => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const listingConfig = LISTING_METADATA[userType]?.canList || {};

  return Object.entries(listingConfig)
    .filter(([, config]) => config.cardinality !== CARDINALITIES.NONE)
    .map(([listingType]) => listingType);
};
/**
 * Get the listing types a user can create based on their user type.
 * @returns {Array<string>} - An array of listing types the user can create.
 * @param currentUser
 */
export const getCreatableListingTypes = userType => {
  const userMetadata = LISTING_METADATA[userType];

  if (!userMetadata) return [];

  return Object.entries(userMetadata.canList)
    .filter(([, config]) => config.cardinality !== CARDINALITIES.NONE)
    .map(([listingType]) => listingType);
};
/**
 * Get the listing categories that can be chosen based on userType and listingTypeId.
 * @param {string} userType - The user type (e.g., 'fitness-venue', 'freelance-personal-trainer').
 * @param {string} listingTypeId - The listing type (e.g., 'class', 'personal-trainer', 'provider-page').
 * @returns {Array<Object>} - An array of listing category objects with id and label.
 */
export const getAvailableCategoriesForListing = (userType, listingTypeId) => {
  const userMetadata = LISTING_METADATA[userType];

  if (!userMetadata) return [];

  const listingConfig = userMetadata.canList[listingTypeId];

  if (!listingConfig || listingConfig.cardinality === CARDINALITIES.NONE) {
    return [];
  }

  return listingConfig.categories.map(({ id }) => id);
};
/**
 * Get the listing categories that can be chosen based on userType and listingTypeId.
 * @param {string} userType - The user type (e.g., 'fitness-venue', 'freelance-personal-trainer').
 * @param {Array<Object>} providerListings - The listing type (e.g., 'class', 'personal-trainer', 'provider-page').
 * @returns {boolean} - An array of listing category objects with id and label.
 */
export const doesProviderHavePrerequisiteListing = (userType, providerListings) => {
  console.log('providerListings');
  console.log(providerListings);
  const { data: listingData } = providerListings;

  if (!listingData?.data) {
    return false;
  }

  listingData.data.some(listing => {
    listing.attributes.publicData.listingType;
    return LISTING_METADATA[userType];
  });

  switch (userType) {
    case USER_TYPES.BROWSER:
      return false;
    case USER_TYPES.INDEPENDENT_CLASS_PROVIDER:
      return true;
    case USER_TYPES.SPORTS_CLUB || USER_TYPES.FITNESS_VENUE:
      return false;
    case USER_TYPES.FREELANCE_PERSONAL_TRAINER:
      return false;
    default:
      return false;
  }
};
/**
 * Fetch the listing types a user is allowed to create based on their userType and current listings.
 * @param {string} userType - The type of user (e.g., FITNESS_VENUE, FREELANCE_PERSONAL_TRAINER).
 * @param {{data: {data: *[]}} | *[]} listingData - An array of listing objects with nested listingType in publicData.
 * @returns {string[]} - An array of listing types the user can create.
 */
export const getAllowedListings = (userType, listingData) => {
  const userConfig = LISTING_METADATA[userType]?.canList;
  if (!userConfig) return [];

  const currentListings = Array.isArray(listingData?.data?.data) ? listingData.data.data : [];

  // Extract listing types and their counts
  const listingCounts = currentListings.reduce((acc, listing) => {
    const listingType = listing.attributes?.publicData?.listingType;
    if (listingType) {
      acc[listingType] = (acc[listingType] || 0) + 1;
    }
    return acc;
  }, {});

  return Object.entries(userConfig)
    .filter(([listingType, config]) => {
      const { cardinality, requires } = config;
      const currentCount = listingCounts[listingType] || 0;

      // Check cardinality limit
      if (cardinality === CARDINALITIES.NONE) return false;
      if (cardinality === CARDINALITIES.ONE && currentCount >= 1) return false;
      if (cardinality === CARDINALITIES.MANY && requires.length === 0) return true;

      // Check dependencies
      return requires.every(requiredType => listingCounts[requiredType] > 0);
    })
    .map(([listingType]) => listingType);
};
/**
 * Get the parent listing ID for a given userType based on current listings.
 * @param {string} userType - The type of user (e.g., FITNESS_VENUE, FREELANCE_PERSONAL_TRAINER).
 * @param {Object} listingData - Object containing the listings data structure.
 * @returns {Object|null} - The listing ID of the parent listing or null if no parent is required or found.
 */
export const getParentListing = (userType, listingData) => {
  const listingsArray = Array.isArray(listingData?.data?.data) ? listingData.data.data : [];

  let parentListingType;

  switch (userType) {
    case USER_TYPES.SPORTS_CLUB:
    case USER_TYPES.FITNESS_VENUE:
      parentListingType = LISTING_TYPES.VENUE_PAGE;
      break;
    case USER_TYPES.FREELANCE_PERSONAL_TRAINER:
      parentListingType = LISTING_TYPES.PERSONAL_TRAINER;
      break;
    case USER_TYPES.INDEPENDENT_CLASS_PROVIDER:
    default:
      return null;
  }

  // Find the first matching parent listing
  const parentListing = listingsArray.find(
    listing => listing.attributes?.publicData?.listingType === parentListingType
  );

  return parentListing || null;
};

/**
 * Retrieves the listing category configuration based on the listing's primary category.
 * Converts the category to uppercase snake_case to match keys in LISTING_CATEGORIES.
 *
 * @param {Object} listing - The listing object containing publicData with categoryLevel1.
 * @returns {Object|undefined} - The corresponding category config from LISTING_CATEGORIES.
 */
export const getListingCategory = listing => {
  return LISTING_CATEGORIES[
    snakeCase(listing?.attributes?.publicData?.categoryLevel1).toUpperCase()
  ];
};

/**
 * Determines if a given field should be displayed based on listing type and category.
 *
 * @param {string} listingType - The type of the listing (e.g. 'event', 'venue').
 * @param {string[]} categoryIds - An array of category IDs to check against.
 * @param {Object} fieldConfig - The field's configuration object.
 * @returns {boolean} - True if the field is valid for the given listing type and category.
 */
export const isEligibleField = (listingType, categoryIds, fieldConfig) =>
  isFieldForListingType(listingType, fieldConfig) && isFieldForCategory(categoryIds, fieldConfig);

/**
 * Retrieves the listing category configuration from a category id.
 *
 * @param {string} id - The categoryLevel1 id.
 * @returns {Object|null} - The corresponding category config from LISTING_CATEGORIES.
 */

export const getListingCategoryById = id => {
  const categories = Object.values(LISTING_CATEGORIES);
  return categories.find(category => category.id === id) || null;
};

/**
 * Retrieves the listing category configuration from a category id.
 *
 * @param {string} id - The listing object containing publicData with categoryLevel1.
 * @returns {Object|null} - The corresponding category config from LISTING_CATEGORIES.
 */

export const getSportsClubSubCategoryById = id => {
  const categories = Object.values(SPORTS_CLUB_SUB_CATEGORIES);
  return categories.find(category => category.id === id) || null;
};

export const getListingCategoryLabel = (listingCategoryId, publicData) => {
  return listingCategoryId === LISTING_CATEGORIES.GYM.id && publicData?.gymType === 'studio'
    ? 'Studio'
    : getListingCategoryById(listingCategoryId)?.label;
};
